import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";

// v1.0.0 Item 11 — Status Dot Legend
// Collapsible card above the 12 groups. Open by default on first visit,
// persists dismissed state in localStorage (key: `bdl_legend_collapsed`).
// Batch C wires actual dot triggers per panel; Batch B ships the legend surface.

const DOT_LEGEND = [
  { color: "red",    hex: "bg-red-500",        label: "Critical",       desc: "Failed jobs, critical alerts, flagged content, security issues. Immediate action required.", pulse: true },
  { color: "orange", hex: "bg-orange-500",     label: "Urgent",         desc: "Action needed today. Items aging in queue, approaching thresholds, warnings becoming red soon." },
  { color: "yellow", hex: "bg-amber-400",      label: "Attention",      desc: "Stale data, pending items, soft warnings. Needs attention soon but not urgent." },
  { color: "green",  hex: "bg-emerald-500",    label: "Healthy",        desc: "All clear. Everything processed, no action needed, panel is healthy." },
  { color: "blue",   hex: "bg-sky-500",        label: "New Info",       desc: "Something changed since your last visit. No action required — informational update." },
  { color: "purple", hex: "bg-purple-500",     label: "In Progress",    desc: "A job is running, a batch is processing, something is actively working." },
  { color: "gray",   hex: "bg-slate-400",      label: "Inactive",       desc: "Panel is quiet, feature not yet active, or no data exists yet." },
  { color: "black",  hex: "bg-slate-800",      label: "Locked",         desc: "Restricted access. Panel requires elevated permissions or is in Danger Zone state." },
];

export default function LegendCard() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("bdl_legend_collapsed") === "1");

  useEffect(() => {
    localStorage.setItem("bdl_legend_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="admin-legend-card">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full px-5 py-3 flex items-center gap-3 hover:bg-slate-50 transition"
        data-testid="legend-toggle"
      >
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Info className="w-4 h-4 text-slate-500" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-sm font-bold text-slate-800">Status Dot Legend</p>
          <p className="text-[10px] text-slate-400">
            {collapsed ? "8 status indicators — tap to expand" : "Tap any row to learn what the dot color means"}
          </p>
        </div>
        {/* Mini row of all 8 dots as a visual preview */}
        <div className="flex items-center gap-1 mr-1">
          {DOT_LEGEND.map((d) => (
            <span key={d.color} className={`w-2 h-2 rounded-full ${d.hex}`} />
          ))}
        </div>
        {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {!collapsed && (
        <div className="px-5 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="legend-body">
          {DOT_LEGEND.map((d) => (
            <div key={d.color} className="flex items-start gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition">
              <span
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${d.hex} ${d.pulse ? "animate-pulse" : ""}`}
                data-testid={`legend-dot-${d.color}`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold text-slate-800 uppercase tracking-wide leading-tight">{d.label}</p>
                <p className="text-[10px] text-slate-500 leading-snug mt-0.5">{d.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
